import type { Locale } from "shared-core/services/i18n-types.ts";
import { AppStatus, SystemId } from "shared-core/types/admin";
import { IntegrationStatusEnum } from "shared-core/types/integrations";
import { IdentityProvider } from "shared-core/types/organization";
import { formatDate } from "shared-core/utils/format.ts";
import { getSystemDisplayName } from "shared-ui/utils/system-visuals.ts";
import { reportUnreachableCase } from "shared-ui/utils/unreachable-case.ts";
import type { Composer } from "vue-i18n";
import type { Router } from "vue-router";
import type { StatusVariant } from "../types/admin";
import type { EmployeeDataMainStore } from "../types/main-store";
import { ADMIN_ROUTES } from "./admin-routes";

export function getDeactivatedStatusVariant(
  deactivatedAt: string | null,
): StatusVariant {
  return deactivatedAt ? "orange" : "gray";
}

export function getInactiveStatusLabel(
  employee: EmployeeDataMainStore,
  t: Composer["t"],
  locale: Locale,
): string {
  if (!employee.deactivatedAt) {
    return t("admin_employees.status_suspended_externally");
  }
  if (!employee.reactivateAt) return t("admin_employees.status_deactivated");
  return t("admin_employees.status_deactivated_until", {
    date: formatDate(new Date(employee.reactivateAt), locale),
  });
}

export function resolveApiErrorDetail(
  error: unknown,
  t: Composer["t"],
  te: Composer["te"],
  fallbackKey = "admin_common.error",
): string {
  if (error instanceof Error && te(error.message)) {
    return t(error.message);
  }
  return t(fallbackKey);
}

export function getIntegrationProviderLabel(
  providers: IdentityProvider[],
  locale: Locale,
): string | null {
  const providerNames = new Set<string>();
  for (const provider of providers) {
    if (provider === IdentityProvider.GOOGLE) {
      providerNames.add("Google Workspace");
    } else if (provider === IdentityProvider.MICROSOFT) {
      providerNames.add("Microsoft 365");
    }
  }
  if (providerNames.size === 0) return null;

  return new Intl.ListFormat(locale, {
    style: "long",
    type: "conjunction",
  }).format([...providerNames]);
}

export function getEmployeeDisplayName(
  employee: EmployeeDataMainStore,
): string {
  return employee.fullName?.trim() || employee.email;
}

export function getReminderLabel(
  lastReminderSent: string | null,
  invite: boolean,
  t: Composer["t"],
): string {
  if (invite) return t("admin_employees.invite_action");
  if (!lastReminderSent) return t("admin_employees.remind_action");
  const diffMs = Date.now() - new Date(lastReminderSent).getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours < 1) return t("admin_employees.reminded_just_now");
  if (hours < 24) return t("admin_employees.reminded_hours_ago", { hours });
  const days = Math.floor(hours / 24);
  return t("admin_employees.reminded_ago", { days });
}

export function getAppStatusVariant(status: AppStatus): StatusVariant {
  switch (status) {
    case AppStatus.Updated:
      return "green";
    case AppStatus.UpdateAvailable:
      return "blue";
    case AppStatus.Inactive:
      return "orange";
    case AppStatus.NotInstalled:
      return "gray";
    default:
      return reportUnreachableCase(status, "gray");
  }
}

export function getAppStatusLabel(status: AppStatus, t: Composer["t"]): string {
  switch (status) {
    case AppStatus.Updated:
      return t("device_inventory.status_updated");
    case AppStatus.UpdateAvailable:
      return t("device_inventory.status_update_available");
    case AppStatus.Inactive:
      return t("device_inventory.status_inactive");
    case AppStatus.NotInstalled:
      return t("device_inventory.status_not_installed");
    default:
      return reportUnreachableCase(status, "");
  }
}

/** Look up translated name & subtitle for a system catalog entry. */
export function getSystemDisplayInfo(
  integrationId: SystemId,
  t: Composer["t"],
  te: Composer["te"],
): { name: string; subtitle: string } {
  return {
    name: te(`system_catalog.${integrationId}.name`)
      ? t(`system_catalog.${integrationId}.name`)
      : getSystemDisplayName(integrationId),
    subtitle: te(`system_catalog.${integrationId}.subtitle`)
      ? t(`system_catalog.${integrationId}.subtitle`)
      : "",
  };
}

/**
 * Triggers the appropriate connect action for a given system.
 * Handles GWS, M365, Meta (OAuth flows) and WordPress (navigation).
 */
export function connectToIntegration(
  systemId: string,
  integrationStore: {
    connectToGoogleWorkspace: () => void;
    connectToMicrosoft365: () => void;
    connectToFacebook: () => void;
    downloadWordPressPlugin: () => void;
  },
  router: Router,
): void {
  if (systemId === SystemId.GOOGLE_WORKSPACE.toString())
    integrationStore.connectToGoogleWorkspace();
  else if (systemId === SystemId.MICROSOFT_365.toString())
    integrationStore.connectToMicrosoft365();
  else if (systemId === SystemId.META_BUSINESS_SUITE.toString())
    integrationStore.connectToFacebook();
  else if (systemId === SystemId.WORDPRESS.toString())
    void router.push(ADMIN_ROUTES.systemDetail(systemId));
}

export function getIntegrationStatusClasses(
  status: IntegrationStatusEnum | undefined,
): string {
  switch (status) {
    case IntegrationStatusEnum.CONNECTED:
      return "bg-green-100 text-green-800";
    case IntegrationStatusEnum.AVAILABLE:
      return "bg-blue-100 text-blue-800";
    case IntegrationStatusEnum.INVALID_TOKEN:
    case IntegrationStatusEnum.PERMISSION_MISSING:
      return "bg-red-100 text-red-800";
    case IntegrationStatusEnum.COMING_SOON:
    case undefined:
      return "bg-gray-100 text-gray-600";
    default:
      return reportUnreachableCase(status, "bg-gray-100 text-gray-600");
  }
}
